import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Plus, MoreVertical, Copy, Trash2, AlertTriangle, Loader2, GraduationCap } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import EditableCell from "./EditableCell";
import {
  FAMIGLIE,
  PARAM_LABELS,
  PARAM_SHORT,
  T1_OBB,
  T1_NA,
  orderedParamColumns,
} from "../../lib/kb";
import { unwrapParam, getValueFrom, getSourceFrom } from "../../lib/vociSchema";
import useColumnResize from "../../hooks/useColumnResize";

// Default pixel widths chosen so that the most common full Italian names
// (e.g. "Acciaio P235GH", "Vapore saturo", "Manuale volantino") fit
// WITHOUT truncation. Operator can drag-resize any column; the choice
// persists to localStorage via useColumnResize.
export const PARAM_DEFAULT_WIDTHS = {
  P01_DN: 75,
  P06_PN: 75,
  P30_spessore: 90,
  P31_schedule: 85,
  P32_lunghezza_mm: 95,
  P10_materiale: 130,
  P11_tenuta: 90,
  P02_connessione: 110,
  P03_filettatura: 115,
  P13_tipo_otturatore: 95,
  P04_flangia_norma: 110,
  P34_tipo_flangia: 110,
  P35_finitura_facing: 70,
  P33_forma_fondello: 105,
  P36_tipo_valvola_reg: 145,
  P37_kv_rated: 85,
  P38_range_taratura: 115,
  P39_dim_lxl: 115,
  P40_finitura_superficie: 120,
  P41_tipo_strumento: 130,
  P42_attacco_strumento: 90,
  P43_portata_m3h: 95,
  P44_prevalenza_m: 90,
  P45_potenza_kw: 85,
  P46_alimentazione: 110,
  P47_tipo_scambiatore: 115,
  P48_tipo_accessorio: 135,
  P16_azionamento: 120,
  P18_fluido: 115,
  P08_TS: 65,
  P22_certificazioni: 150,
};

export const FIXED_DEFAULT_WIDTHS = {
  __idx: 40,
  __tipo: 150,
  __quantita: 55,
  __codice: 160,
  __prezzo: 95,
  __menu: 32,
};

// R8-quater BUG 1 v0.5.4: human-readable label per ogni famiglia
// dal sottotipo specifico (P28 o P34 o sotto_tipo). Ritorna sempre
// una stringa per le famiglie note.
// R8-quinquies v0.5.5: copertura allargata a tutte le famiglie.
const TIPO_RACCORDO_LABEL = {
  curva_90: "Curva 90° a saldare",
  curva_45: "Curva 45° a saldare",
  tee_uguale: "Tee uguale a saldare",
  tee_ridotto: "Tee ridotto a saldare",
  riduzione_concentrica: "Riduzione concentrica",
  riduzione_eccentrica: "Riduzione eccentrica",
  fondello: "Fondello a saldare",
  cartella: "Cartella a saldare",
  manicotto_saldare: "Manicotto a saldare",
};

const TIPO_FLANGIA_LABEL = {
  "Slip-On": "Flangia slip-on",
  "Piana": "Flangia piana",
  "WN": "Flangia welding-neck",
  "Welding-neck": "Flangia welding-neck",
  "Cieca": "Flangia cieca",
  "A collarino": "Flangia a collarino",
  "Type 01": "Flangia EN 1092-1 Type 01",
  "Type 11": "Flangia EN 1092-1 Type 11",
};

function tipoProdottoLabel(voce) {
  const fam = voce.famiglia || "F05";
  const sotto = voce.sotto_tipo || "";
  const estr = voce.parametri_estratti || {};
  const inf = voce.parametri_inferiti || {};

  // F02: derive from P28_tipo_raccordo
  if (fam === "F02") {
    const p28 = getValueFrom(estr, "P28_tipo_raccordo") || getValueFrom(inf, "P28_tipo_raccordo");
    if (p28 && TIPO_RACCORDO_LABEL[p28]) return TIPO_RACCORDO_LABEL[p28];
    return sotto ? capitalize(sotto) : "Raccordo a saldare";
  }
  // F04: derive from P34_tipo_flangia
  if (fam === "F04") {
    const p34 = getValueFrom(estr, "P34_tipo_flangia") || getValueFrom(inf, "P34_tipo_flangia");
    if (p34 && TIPO_FLANGIA_LABEL[p34]) return TIPO_FLANGIA_LABEL[p34];
    if (p34) return `Flangia ${String(p34).toLowerCase()}`;
    return sotto ? capitalize(sotto) : "Flangia";
  }
  // F05: sotto_tipo è già descrittivo ("valvola a sfera", "saracinesca"…)
  if (fam === "F05") {
    return sotto ? capitalize(sotto) : "Valvola di intercettazione";
  }
  // F03 filettati / F01 tubi / altri: sotto_tipo è la single source
  return sotto ? capitalize(sotto) : null;
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusFor(voce, pcode) {
  if (pcode in (voce.parametri_estratti || {})) return "extracted";
  if (pcode in (voce.parametri_inferiti || {})) return "default";
  if ((voce.parametri_mancanti_critici || []).includes(pcode)) return "missing";
  return "confirmed";
}

// R8 — motivazione breve (tooltip azzurro) associata al default software
// per il pcode dato. Ritorna "" se la cella è VERDE/ROSSA/NORMALE.
function motivazioneFor(voce, pcode) {
  const motiv = voce._motivazioni || {};
  return motiv[pcode] || "";
}

function valueFor(voce, pcode) {
  // R11/D hotfix — unwrap dict-shape so React doesn't crash
  return (
    unwrapParam(voce.parametri_estratti?.[pcode]) ??
    unwrapParam(voce.parametri_inferiti?.[pcode]) ??
    ""
  );
}

// Score badge — always visible next to the top-1 code in the selected cell
// and to the right of each candidato in the dropdown.
function ScoreBadge({ confidenza, testId }) {
  if (confidenza == null) return null;
  const score = Number(confidenza);
  let bg, fg, tone;
  if (score >= 85) {
    bg = "#DCFCE7";
    fg = "#16A34A";
    tone = "high";
  } else if (score >= 60) {
    bg = "#FEF3C7";
    fg = "#D97706";
    tone = "mid";
  } else {
    bg = "#FEE2E2";
    fg = "#DC2626";
    tone = "low";
  }
  return (
    <span
      className="tq-cell-mono text-[11px] font-medium rounded-full whitespace-nowrap"
      style={{
        background: bg,
        color: fg,
        padding: "2px 6px",
        lineHeight: 1.2,
      }}
      data-testid={testId}
      data-score-tone={tone}
    >
      {score.toFixed(0)}%
    </span>
  );
}

// R8-quater Fix 6: ConfidenceBadge — sostituisce lo score % numerico con
// 3 livelli colorati ESATTO 🟢 / PROSSIMO 🟡 / VARIANTE 🔴.
// `deltas` opzionale → tooltip con scarti specifici (es. "DN req 25 / got 20 / scarto 20%").
function ConfidenceBadge({ level, deltas, testId }) {
  if (!level) return null;
  const palette = {
    ESATTO:   { bg: "#DCFCE7", fg: "#16A34A", border: "#16A34A" },
    PROSSIMO: { bg: "#FEF3C7", fg: "#B45309", border: "#D97706" },
    VARIANTE: { bg: "#FEE2E2", fg: "#B91C1C", border: "#DC2626" },
  };
  const c = palette[level] || palette.VARIANTE;
  // Build tooltip from deltas if available
  let tooltip = "";
  if (deltas && typeof deltas === "object") {
    const lines = Object.entries(deltas).map(([pcode, info]) => {
      if (!info) return null;
      const req = info.req;
      const got = info.got;
      const dp = info.delta_pct;
      const lvl = info.level || "";
      const lbl = pcode.replace(/^P\d+_/, "");
      if (dp != null) {
        return `${lbl} richiesto: ${req} | trovato: ${got} | scarto: ${dp}% (${lvl})`;
      }
      return `${lbl} richiesto: ${req} | trovato: ${got} (${lvl})`;
    }).filter(Boolean);
    if (lines.length) tooltip = lines.join("\n");
  }
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-wider rounded-full whitespace-nowrap"
      style={{
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        padding: "1px 6px",
        lineHeight: 1.3,
      }}
      data-testid={testId}
      data-confidence-level={level}
      title={tooltip || level}
    >
      {level}
    </span>
  );
}

function CodiceCell({
  voce,
  onSelectCandidate,
  onMarkCustom,
  fieldsMissing,
  sticky = false,
  stickyLeft = 0,
}) {
  const candidati = voce.candidati || [];
  // Wrap helper — applies sticky left positioning + a heavier right border so
  // the operator always sees the codice column even when scrolling x-axis.
  const wrapStyle = sticky
    ? {
        position: "sticky",
        left: stickyLeft,
        zIndex: 2,
        background: "var(--bg-surface)",
      }
    : {};
  const wrapCls = sticky ? "tq-cell-sticky" : "";

  // v0.4.2+ — "non_a_listino" explicit state: all OBB fields are filled but
  // no catalogue match. Rendered in amber so it stands out from red (missing)
  // and gray (manual).
  if (
    !fieldsMissing &&
    !voce._is_custom &&
    !voce._matching &&
    voce._match_attempted &&
    candidati.length === 0
  ) {
    return (
      <div
        className={`tq-cell tq-cell-status-confirmed flex items-center justify-between gap-2 ${wrapCls}`}
        style={wrapStyle}
        data-testid={`voce-${voce.idx}-codice-non-listino`}
        title="Nessun articolo a listino con queste specifiche. Verificare specs o impostare preventivo manuale."
      >
        <span
          className="text-[12px] truncate"
          style={{ color: "#B45309" }}
        >
          ⚠ Non a listino
        </span>
        <button
          type="button"
          onClick={() => onMarkCustom?.(voce.idx)}
          className="text-[11px] font-medium whitespace-nowrap"
          style={{ color: "var(--accent-primary)" }}
          data-testid={`voce-${voce.idx}-mark-custom`}
        >
          preventivo manuale
        </button>
      </div>
    );
  }

  if (fieldsMissing) {
    // R8-bis — niente più "Compila: X" rosso (logica T1_OBB legacy).
    // Mostriamo "⚠ Non a listino" giallo + bottone preventivo manuale,
    // identico al ramo no-candidati post-match. L'operatore può
    // valorizzare le celle rosse residue dalla griglia (date consegna,
    // cert opzionali) ma non bloccare il flusso.
    return (
      <div
        className={`tq-cell tq-cell-status-confirmed flex items-center justify-between gap-2 ${wrapCls}`}
        style={wrapStyle}
        data-testid={`voce-${voce.idx}-codice-non-listino`}
        title="Compila i campi rossi residui o imposta come preventivo manuale."
      >
        <span
          className="text-[12px] truncate"
          style={{ color: "#B45309" }}
        >
          ⚠ Non a listino
        </span>
        <button
          type="button"
          onClick={() => onMarkCustom?.(voce.idx)}
          className="text-[11px] font-medium whitespace-nowrap"
          style={{ color: "var(--accent-primary)" }}
          data-testid={`voce-${voce.idx}-mark-custom`}
        >
          preventivo manuale
        </button>
      </div>
    );
  }
  if (voce._is_custom) {
    return (
      <div
        className={`tq-cell tq-cell-status-confirmed italic text-[12px] flex items-center ${wrapCls}`}
        style={{ color: "var(--text-muted)", ...wrapStyle }}
        data-testid={`voce-${voce.idx}-codice`}
      >
        ✏ preventivo manuale
      </div>
    );
  }
  if (voce._matching) {
    return (
      <div
        className={`tq-cell tq-cell-status-confirmed flex items-center gap-1.5 text-[12px] ${wrapCls}`}
        style={{ color: "var(--text-secondary)", ...wrapStyle }}
        data-testid={`voce-${voce.idx}-codice-loading`}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Cerco codice…
      </div>
    );
  }
  if (!candidati.length) {
    return (
      <div
        className={`tq-cell tq-cell-status-confirmed italic text-[12px] flex items-center ${wrapCls}`}
        style={{ color: "var(--text-muted)", ...wrapStyle }}
        data-testid={`voce-${voce.idx}-codice`}
      >
        in attesa…
      </div>
    );
  }
  const selected = candidati.find((c) => c.selezionato) || candidati[0];
  return (
    <div
      className={`tq-cell tq-cell-status-confirmed py-1 ${wrapCls}`}
      style={wrapStyle}
      data-testid={`voce-${voce.idx}-codice`}
    >
      <Select
        value={selected.codice}
        onValueChange={(v) => onSelectCandidate(v)}
      >
        <SelectTrigger
          className="h-auto py-0 px-0 border-0 bg-transparent shadow-none focus:ring-0 text-[12px] [&>svg]:hidden"
          style={{ color: "var(--accent-primary)" }}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className="tq-cell-mono truncate"
              style={{ color: "var(--accent-primary)" }}
            >
              {selected.codice}
            </span>
            <span
              className="text-[11px]"
              style={{ color: "var(--text-muted)" }}
            >
              ·
            </span>
            <ConfidenceBadge
              level={selected.confidence_level}
              deltas={selected.confidence_deltas}
              testId={`voce-${voce.idx}-confidence-badge`}
            />
            <ScoreBadge
              confidenza={selected.confidenza}
              testId={`voce-${voce.idx}-score-badge`}
            />
          </div>
        </SelectTrigger>
        <SelectContent className="w-[460px]">
          {candidati.map((c) => (
            <SelectItem key={c.codice} value={c.codice}>
              <div className="flex items-center justify-between gap-3 w-full">
                <div className="flex flex-col min-w-0">
                  <span
                    className="tq-cell-mono text-[12px]"
                    style={{ color: "var(--accent-primary)" }}
                  >
                    {c.codice}
                  </span>
                  <span
                    className="text-[11px] truncate max-w-[320px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {c.descrizione}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ConfidenceBadge
                    level={c.confidence_level}
                    deltas={c.confidence_deltas}
                    testId={`candidato-${c.codice}-confidence-badge`}
                  />
                  <ScoreBadge
                    confidenza={c.confidenza}
                    testId={`candidato-${c.codice}-score-badge`}
                  />
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ResizeHandle sits on the right edge of a header cell.
function ResizeHandle({ colKey, startResize, resetOne }) {
  const onDoubleClick = useCallback(() => resetOne(colKey), [colKey, resetOne]);
  const onMouseDown = useCallback(
    (e) => startResize(colKey, e),
    [colKey, startResize]
  );
  return (
    <span
      className="tq-resize-handle"
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Ridimensiona colonna ${colKey}`}
      data-testid={`resize-handle-${colKey}`}
    />
  );
}

function HeaderCell({
  colKey,
  label,
  titleAttr,
  align = "left",
  sticky = false,
  stickyLeft = null,
  resizable = true,
  startResize,
  resetOne,
  withShadow = false,
}) {
  const style = { borderRight: "1px solid var(--border-subtle)" };
  if (sticky) {
    style.position = "sticky";
    style.left = `${stickyLeft}px`;
    style.background = "var(--bg-subtle)";
    style.zIndex = 3;
  }
  const alignCls = align === "right" ? "text-right" : "text-left";
  const cls = `px-2 py-1.5 truncate relative ${alignCls} ${
    sticky ? "tq-cell-sticky" : ""
  } ${withShadow ? "tq-cell-sticky-shadow" : ""}`;
  return (
    <div className={cls} style={style} title={titleAttr || label}>
      {label}
      {resizable && (
        <ResizeHandle
          colKey={colKey}
          startResize={startResize}
          resetOne={resetOne}
        />
      )}
    </div>
  );
}

const VociGrid = forwardRef(function VociGrid(
  {
    voci,
    onUpdateVoce,
    onAddVoce,
    onRemoveVoce,
    onDuplicateVoce,
    onMarkCustom,
    onSelectCandidate,
    onTeachVoce,           // R11.b — kebab "Insegna su questa voce"
    onLearnedRuleClick,    // R11.b — click on learned cell badge
    canTeach = false,      // R11.b — RBAC: titolare+super_admin
  },
  ref
) {
  // Compute paramColumns as the union of obligatory params for all voci families.
  const paramColumns = useMemo(() => {
    const families = [...new Set(voci.map((v) => v.famiglia || "F05"))];
    const set = new Set();
    families.forEach((f) => orderedParamColumns(f).forEach((p) => set.add(p)));
    if (set.size === 0) orderedParamColumns("F05").forEach((p) => set.add(p));
    const canonical = orderedParamColumns(families[0] || "F05");
    return canonical
      .filter((p) => set.has(p))
      .concat([...set].filter((p) => !canonical.includes(p)));
  }, [voci]);

  const defaults = useMemo(() => {
    const d = { ...FIXED_DEFAULT_WIDTHS };
    paramColumns.forEach((p) => {
      d[p] = PARAM_DEFAULT_WIDTHS[p] || 110;
    });
    return d;
  }, [paramColumns]);

  const { widths, startResize, resetOne, reset } = useColumnResize(defaults, {
    min: 40,
    max: 400,
  });

  useImperativeHandle(ref, () => ({ resetWidths: reset }), [reset]);

  // Build grid-template-columns from current widths + column order
  // Post-v0.4.2 order: CODICE LISTINO is first (sticky) so operators always
  // see the item code even when scrolling far right in param-heavy families.
  const gridTemplate = useMemo(() => {
    const parts = [
      `${widths.__codice || 160}px`,
      `${widths.__idx || 40}px`,
      `${widths.__tipo || 150}px`,
      ...paramColumns.map((p) => `${widths[p] || defaults[p] || 110}px`),
      `${widths.__quantita || 55}px`,
      `${widths.__prezzo || 95}px`,
      `${widths.__menu || 32}px`,
    ];
    return parts.join(" ");
  }, [widths, paramColumns, defaults]);

  // Scroll-overflow detection — drives fade + sticky-shadow classes
  const scrollerRef = useRef(null);
  const [overflowState, setOverflowState] = useState({ left: false, right: false });
  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflowState((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right }
    );
  }, []);
  useEffect(() => {
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateOverflow, { passive: true });
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    window.addEventListener("resize", updateOverflow);
    return () => {
      el.removeEventListener("scroll", updateOverflow);
      ro.disconnect();
      window.removeEventListener("resize", updateOverflow);
    };
  }, [updateOverflow, gridTemplate]);

  const voiceHasMissing = (v) =>
    (v.parametri_mancanti_critici || []).length > 0;
  const stickyCodiceWidth = widths.__codice || 160;
  const stickyIdxLeft = stickyCodiceWidth;
  const stickyTipoLeft = stickyIdxLeft + (widths.__idx || 40);

  const scrollWrapCls = [
    "tq-grid-scroll-wrap",
    "overflow-x-auto",
    "tq-scroll",
    overflowState.left ? "has-left-overflow tq-grid-scrolled" : "",
    overflowState.right ? "has-right-overflow" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className="rounded-lg bg-white overflow-hidden"
      style={{
        border: "1px solid var(--border-subtle)",
        boxShadow: "var(--shadow-sm)",
      }}
      data-testid="voci-grid"
    >
      <div ref={scrollerRef} className={scrollWrapCls}>
        <div style={{ minWidth: "fit-content" }}>
          {/* Header */}
          <div
            className="grid tq-header-row text-[11px] font-semibold uppercase tracking-wide"
            style={{
              gridTemplateColumns: gridTemplate,
              background: "var(--bg-subtle)",
              borderBottom: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            <HeaderCell
              colKey="__codice"
              label="Codice listino"
              titleAttr="Codice listino"
              sticky
              stickyLeft={0}
              startResize={startResize}
              resetOne={resetOne}
            />
            <HeaderCell
              colKey="__idx"
              label="#"
              titleAttr="Numero"
              sticky
              stickyLeft={stickyIdxLeft}
              resizable={false}
              startResize={startResize}
              resetOne={resetOne}
            />
            <HeaderCell
              colKey="__tipo"
              label="Tipo prodotto"
              sticky
              stickyLeft={stickyTipoLeft}
              withShadow
              startResize={startResize}
              resetOne={resetOne}
            />
            {paramColumns.map((p) => (
              <HeaderCell
                key={p}
                colKey={p}
                label={PARAM_LABELS[p] || p}
                startResize={startResize}
                resetOne={resetOne}
              />
            ))}
            <HeaderCell
              colKey="__quantita"
              label="Qtà"
              align="right"
              startResize={startResize}
              resetOne={resetOne}
            />
            <HeaderCell
              colKey="__prezzo"
              label="Prezzo"
              align="right"
              startResize={startResize}
              resetOne={resetOne}
            />
            <div className="px-1 py-1.5" />
          </div>

          {/* Rows */}
          {voci.map((v) => {
            const missing = voiceHasMissing(v);
            const selectedCand =
              v.candidati?.find((c) => c.selezionato) || v.candidati?.[0];
            const prezzo = selectedCand ? selectedCand.prezzo : null;
            const obbForVoce = new Set(T1_OBB[v.famiglia] || []);
            return (
              <div
                key={v.idx}
                className="grid tq-row-zebra transition-colors"
                style={{ gridTemplateColumns: gridTemplate }}
                data-testid={`voce-row-${v.idx}`}
              >
                <CodiceCell
                  voce={v}
                  fieldsMissing={missing}
                  onMarkCustom={onMarkCustom}
                  onSelectCandidate={(cod) => onSelectCandidate(v.idx, cod)}
                  sticky
                  stickyLeft={0}
                />
                <div
                  className="tq-cell tq-cell-status-confirmed tq-cell-sticky tq-cell-mono text-[11px] flex items-center gap-1"
                  style={{ color: "var(--text-muted)", left: `${stickyIdxLeft}px` }}
                >
                  <span>{String(v.idx + 1).padStart(2, "0")}</span>
                  {v.source === "vision_ocr" && (
                    <span
                      title="Riga estratta automaticamente da scansione — verifica DN/PN"
                      data-testid={`voce-${v.idx}-source-badge`}
                      className="inline-flex items-center text-[9px] font-semibold leading-none px-1 py-0.5 rounded-sm"
                      style={{
                        background: "#FEF3C7",
                        color: "#92400E",
                        border: "1px solid #FDE68A",
                      }}
                    >
                      OCR
                    </span>
                  )}
                </div>

                <div
                  className="tq-cell tq-cell-status-confirmed tq-cell-sticky tq-cell-sticky-shadow flex flex-col justify-center"
                  style={{ left: `${stickyTipoLeft}px` }}
                  data-testid={`voce-${v.idx}-tipo`}
                  title={
                    FAMIGLIE[v.famiglia || "F05"] +
                    (v.sotto_tipo ? " · " + v.sotto_tipo : "")
                  }
                >
                  {/* R8-quinquies v0.5.5: gerarchia invertita — sottotipo
                      in evidenza, famiglia (Select editabile) sotto in
                      piccolo grigio. */}
                  <div
                    className="text-[12px] font-medium leading-tight truncate"
                    style={{ color: "var(--text-primary)" }}
                    data-testid={`voce-${v.idx}-tipo-prodotto`}
                  >
                    {tipoProdottoLabel(v) || FAMIGLIE[v.famiglia || "F05"]}
                  </div>
                  <Select
                    value={v.famiglia || "F05"}
                    onValueChange={(val) =>
                      onUpdateVoce(v.idx, setFamiglia(v, val))
                    }
                  >
                    <SelectTrigger
                      className="h-auto py-0 px-0 border-0 bg-transparent shadow-none focus:ring-0 text-[10px] font-normal mt-0.5 [&>svg]:h-2.5 [&>svg]:w-2.5"
                      style={{ color: "var(--text-muted)" }}
                      data-testid={`voce-${v.idx}-famiglia-select`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FAMIGLIE).map(([code, label]) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {paramColumns.map((p) => {
                  // R8: _fields_not_applicable sovrascrive T1_NA legacy.
                  // Le chiavi del backend usano label italiane ("schedule",
                  // "tipo_flangia"…) che non corrispondono ai P-code UI —
                  // quindi la check legacy T1_NA rimane autoritativa qui.
                  const isNa = (T1_NA[v.famiglia] || []).includes(p);
                  if (isNa || !obbForVoce.has(p)) {
                    return (
                      <div
                        key={p}
                        className="tq-cell tq-cell-status-confirmed text-[12px]"
                        style={{
                          color: "var(--text-muted)",
                          background: "var(--bg-subtle)",
                          opacity: 0.7,
                        }}
                        data-testid={`voce-${v.idx}-${p}-na`}
                        title="Non applicabile a questa famiglia"
                      >
                        —
                      </div>
                    );
                  }
                  const isNumeric = p === "P08_TS" || p === "P30_spessore";
                  // Build filters from SIBLING params on the same row so
                  // the dropdown narrows cascadingly (e.g. for F02
                  // materiale only shows what EXISTS for current DN +
                  // spessore).
                  const voceFilters = {};
                  for (const other of paramColumns) {
                    if (other === p) continue;
                    const ov = valueFor(v, other);
                    if (ov !== undefined && ov !== null && ov !== "")
                      voceFilters[other] = ov;
                  }
                  const motivazione = motivazioneFor(v, p);
                  // R11.b — Learned-rule meta lookup
                  const estrRaw = v.parametri_estratti?.[p];
                  const learnedMeta =
                    estrRaw && typeof estrRaw === "object" && estrRaw.source === "learned_rule"
                      ? {
                          rule_id: estrRaw.learned_rule_id,
                          motivazione: estrRaw.motivazione,
                          created_by: estrRaw.created_by_name,
                          created_at: estrRaw.created_at,
                        }
                      : null;
                  return (
                    <EditableCell
                      key={p}
                      pcode={p}
                      value={valueFor(v, p)}
                      status={statusFor(v, p)}
                      onChange={(val) =>
                        onUpdateVoce(v.idx, setParam(v, p, val))
                      }
                      testId={`voce-${v.idx}-${p}`}
                      monoFont={isNumeric || p === "P01_DN" || p === "P06_PN"}
                      famiglia={v.famiglia}
                      voceFilters={voceFilters}
                      tooltipMotivazione={motivazione}
                      learnedRule={learnedMeta}
                      onLearnedRuleClick={onLearnedRuleClick}
                    />
                  );
                })}

                <div
                  className="tq-cell tq-cell-status-confirmed tq-cell-mono text-right text-[12px]"
                  data-testid={`voce-${v.idx}-qty-cell`}
                >
                  <input
                    type="number"
                    min="1"
                    value={v.quantita}
                    onChange={(e) =>
                      onUpdateVoce(v.idx, {
                        ...v,
                        quantita: Math.max(
                          1,
                          parseInt(e.target.value || "1", 10)
                        ),
                      })
                    }
                    className="w-full bg-transparent outline-none border-0 p-0 text-right tq-cell-mono text-[12px]"
                    style={{ color: "var(--text-primary)" }}
                    data-testid={`voce-${v.idx}-qty`}
                  />
                </div>

                <div
                  className="tq-cell tq-cell-status-confirmed tq-cell-mono text-right text-[12px] font-semibold"
                  style={{ color: "var(--text-primary)" }}
                  data-testid={`voce-${v.idx}-prezzo`}
                  title={prezzo != null ? `€ ${prezzo.toFixed(2)}` : ""}
                >
                  {prezzo != null ? `€ ${prezzo.toFixed(2)}` : "—"}
                </div>

                <div
                  className="tq-cell tq-cell-status-confirmed flex items-center justify-center"
                  style={{ padding: "0" }}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="h-5 w-5 inline-flex items-center justify-center rounded transition-colors"
                        style={{ color: "var(--text-muted)" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "var(--bg-subtle)";
                          e.currentTarget.style.color = "var(--text-primary)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "var(--text-muted)";
                        }}
                        data-testid={`voce-${v.idx}-menu`}
                      >
                        <MoreVertical className="h-3 w-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => onDuplicateVoce(v.idx)}>
                        <Copy className="h-3.5 w-3.5 mr-2" /> Duplica
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onMarkCustom(v.idx)}>
                        <AlertTriangle className="h-3.5 w-3.5 mr-2" />
                        Imposta come preventivo manuale (no listino)
                      </DropdownMenuItem>
                      {onTeachVoce && (
                        <DropdownMenuItem
                          onClick={() => onTeachVoce(v)}
                          data-testid={`voce-${v.idx}-teach-btn`}
                        >
                          <GraduationCap className="h-3.5 w-3.5 mr-2" />
                          {canTeach ? "Insegna su questa voce" : "Proponi regola"}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onRemoveVoce(v.idx)}
                        className="text-red-600 focus:text-red-700"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Rimuovi
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add row footer — OUTSIDE the scroller so it stays fixed */}
      <div
        className="px-4 py-2.5 flex items-center gap-3"
        style={{
          background: "var(--bg-app)",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <button
          type="button"
          onClick={onAddVoce}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium transition-colors"
          style={{ color: "var(--text-secondary)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--bg-subtle)";
            e.currentTarget.style.color = "var(--text-primary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--text-secondary)";
          }}
          data-testid="add-voce-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          Aggiungi voce manualmente
        </button>
        <span
          className="text-[11px] ml-auto"
          style={{ color: "var(--text-muted)" }}
        >
          {voci.length} {voci.length === 1 ? "voce" : "voci"}
        </span>
      </div>
    </div>
  );
});

export default VociGrid;

// Recompute parametri_mancanti_critici against the backend T1 for a family.
// P21_settore is handled at session level and NEVER counted as missing here.
function recomputeMancanti(famiglia, estratti, inferiti) {
  const obb = T1_OBB[famiglia] || [];
  return obb.filter((p) => !(p in estratti) && !(p in inferiti));
}

function setParam(voce, pcode, value) {
  const estratti = { ...(voce.parametri_estratti || {}) };
  const inferiti = { ...(voce.parametri_inferiti || {}) };
  if (value === "" || value == null) {
    delete estratti[pcode];
    delete inferiti[pcode];
  } else {
    estratti[pcode] = value;
    delete inferiti[pcode];
  }
  return {
    ...voce,
    parametri_estratti: estratti,
    parametri_inferiti: inferiti,
    parametri_mancanti_critici: recomputeMancanti(
      voce.famiglia,
      estratti,
      inferiti
    ),
    candidati: [],
    _match_attempted: false,
    _matching: false,
  };
}

function setFamiglia(voce, newFamiglia) {
  const estratti = { ...(voce.parametri_estratti || {}) };
  const inferiti = { ...(voce.parametri_inferiti || {}) };
  return {
    ...voce,
    famiglia: newFamiglia,
    parametri_mancanti_critici: recomputeMancanti(
      newFamiglia,
      estratti,
      inferiti
    ),
    candidati: [],
    _match_attempted: false,
    _matching: false,
  };
}
